<template lang="html">
  <v-sheet height="100%" color="bg1" width="100%" id="admin-areas-container">
    <v-sheet height="100%" class="rounded-tl-lg overflow-hidden pa-4 d-flex flex-column">
      <!-- Title Row -->
      <v-row class="flex-grow-0 px-2">
        <v-col cols="12" class="d-flex align-center" :width="$vuetify.breakpoint.xs ? '100%' : 350">
          <h3 class="me-3">Areas</h3>
        </v-col>
      </v-row>

      <!-- Search and Selection Row -->
      <v-row class="flex-grow-0 px-2">
        <v-col cols="12" class="d-flex justify-space-between align-center">
          <!-- Search Bar -->
          <v-sheet width="100%">
            <v-text-field
              style="max-width: 300px;"
              hide-details
              height="40"
              outlined
              v-model="searchtext"
              dense
              class="search-bar"
              placeholder="Search..."
              clearable
              prepend-inner-icon="mdi-magnify" />
          </v-sheet>

          <!-- Area Selection and Action Button -->
          <v-sheet class="d-flex justify-content-end align-center">
            <v-select
              v-has="permissions.Areas.Update"
              label="Selected Area"
              style="width: 300px;"
              class="mr-4"
              dense
              hide-details
              outlined
              v-model="selectedAreaId"
              @change="updateSelectedArea(selectedAreaId)"
              clearable
              item-value="id"
              item-text="name"
              :items="entries" />
            <v-btn @click="addArea()" color="primary">
              <v-icon left>mdi-plus</v-icon> Add Area
            </v-btn>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- Data Table Row -->
      <v-row>
        <v-col cols="12">
          <v-sheet class="px-2">
            <v-data-table
              :headers="headers"
              :items="entries"
              :search="searchtext"
              :items-per-page="15"
              :item-class="itemClassHandler"
              class="elevation-1">
              <!-- Name Column Slot -->
              <template v-slot:[`item.name`]="{ item }">
                <h4 class="d-flex align-center">
                  <v-list-item-avatar size="50" class="rounded-lg">
                    <v-tooltip right :z-index="999" nudge-right="-4px" v-if="item.thumbnailUrl">
                      <template v-slot:activator="{ on, attrs }">
                        <v-img
                          v-bind="attrs"
                          v-on="on"
                          style="cursor: pointer !important"
                          :src="item.thumbnailUrl ?? '/img/no-image.png'"
                          :key="item.id" />
                      </template>
                      <span>
                        <v-img
                          :src="item.thumbnailUrl ?? '/img/no-image.png'"
                          max-height="250px"
                          max-width="250px"
                          contain></v-img>
                      </span>
                    </v-tooltip>
                    <span v-else>
                      <v-img
                        :src="item.thumbnailUrl ?? '/img/no-image.png'"
                        width="50"
                        contain></v-img>
                    </span>
                  </v-list-item-avatar>
                  {{ item.name }}
                </h4>
              </template>

              <!-- Actions Column Slot -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon class="mr-2" v-has="permissions.Areas.Update" @click="editArea(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon class="mr-2" v-has="permissions.Areas.Delete" @click="deleteArea(item)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
                <v-btn :loading="removingTagsOutside" v-has="permissions.Areas.Update" :disabled="!viewer" icon @click="removeTagsOutside(item.id)" title="Remove tags outside the area">
                  <v-icon>mdi-vector-square-remove</v-icon>
                </v-btn>
              </template>

              <!-- No Data Slot -->
              <template v-slot:no-data>
                <div class="text-center d-flex flex-column">
                  <img
                    width="300"
                    src="/img/notFound.svg"
                    class="py-5 my-8"
                    style="opacity: 0.54" />
                  <p class="font-weight-medium">No Data Available!</p>
                </div>
              </template>

              <!-- Loading Slot -->
              <template v-slot:loading>
                <div class="text-center d-flex flex-column">
                  <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
                  <p class="font-weight-medium">Loading Levels...</p>
                </div>
              </template>
            </v-data-table>
          </v-sheet>
        </v-col>
      </v-row>
    </v-sheet>
    <EditArea ref="editArea"></EditArea>
  </v-sheet>
</template>

<script>
  import { log, warn, error } from '@/utils/debugLogger';
  import EditArea from '@/components/admin/popups/EditArea.vue';
  import { mapGetters } from 'vuex';
  import { $has } from '@/utils';
  import permissions from '@Share/config/permissions';

  export default {
    name: 'ModelAreas',
    components: {
      EditArea,
    },
    data() {
      return {
        permissions,
        searchtext: '',
        headers: [
          { text: 'Name', value: 'name' },
          { text: 'Description', value: 'description' },
          { text: 'Actions', value: 'actions', sortable: false },
        ],
        removingTagsOutside: false,
      };
    },
    methods: {
      $has,
      updateSelectedArea(areaId) {
        this.$store.dispatch('AREAS/UPDATE_SELECTED_AREA_ID', areaId).catch((err) => {
          error('Error updating selected area', err);
          this.$dialog.notify.error({
            text: 'Error updating selected area',
            title: 'Error',
          });
        });
      },
      addArea() {
        this.$refs.editArea.open();
      },
      editArea(item) {
        this.$refs.editArea.open(item);
      },
      deleteArea(item) {
        this.$dialog.error({
          text: 'Are you sure you want to delete this area?',
          title: 'Delete Area',
          actions: {
            false: {
              text: 'Yes',
              color: 'error',
              handle: () => {
                this.$store.dispatch('AREAS/DELETE_ENTRY', item.id);
              },
            },
            true: {
              text: 'No',
              color: 'primary',
            },
          },
        });
      },
      removeTagsOutside(areaId) {
        this.$dialog.error({
          text: 'Are you sure you want to remove tags outside this area?',
          title: 'Remove Tags Outside Area',
          actions: {
            false: {
              text: 'Yes',
              color: 'error',
              handle: () => {
                this.removingTagsOutside = true;
                this.$store.dispatch('AREAS/REMOVE_TAGS_OUTSIDE_AREA', areaId).then(() => {
                  this.$dialog.notify.success('Tags outside area removed successfully');
                }).catch((err) => {
                  error('Error removing tags outside area', err);
                  this.$dialog.notify.error('Error removing tags outside area');
                }).finally(() => {
                  this.removingTagsOutside = false;
                });
              },
            },
            true: {
              text: 'No',
              color: 'primary',
            },
          },
        });
      },
      itemClassHandler(item) {
        return {
          'selected-row': item.id === this.selectedAreaId,
        };
      },
    },
    computed: {
      ...mapGetters('AREAS', {
        loading: 'GET_LOADING',
        entries: 'GET_ENTRIES',
        total: 'GET_TOTAL',
      }),
      viewer() {
        return this.$store.state.viewer;
      },
      selectedAreaId: {
        get() {
          return this.$store.getters['AREAS/GET_SELECTED_AREA_ID'];
        },
        set(value) {
          this.$store.commit('AREAS/SET_SELECTED_AREA_ID', value);
        },
      },
    },
    mounted() {
      this.$store.dispatch('AREAS/GET_ENTRIES').catch((err) => {
        error('Error getting areas', err);
        this.$dialog.notify.error({
          text: 'Error getting areas',
          title: 'Error',
        });
      });
    },
  };
</script>
<style lang="scss">
  #admin-areas-container {
    .v-dialog__content {
      position: absolute !important;
    }

    .selected-row {
      background-color: var(--v-primary-lighten2) !important;
    }
  }
</style>
